import { useState } from 'react';

import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Text, Button, Input } from '@ui-component-library';

import { grotStep3ValidationMessages } from './helpers/sellTimberShared';
import { GrotJaNejFieldset } from './components/GrotJaNejFieldset';
import {
  SELL_TIMBER_GROT_STEP3_FIELD_INFO,
  SellTimberFieldInfo
} from './components/SellTimberFieldInfo.js';

export default function SellTimberGrotStep3({ wizard, dispatch }) {
  const initial = wizard.adCreateData ?? {};

  const [grotVolume, setGrotVolume] = useState(
    initial.grot_volume != null ? String(initial.grot_volume) : ''
  );
  const [heapCovered, setHeapCovered] = useState(initial.grot_heap_covered ?? '');
  const [collected, setCollected] = useState(initial.grot_collected ?? '');
  const [stepError, setStepError] = useState(null);

  const selectedProperty = initial.selectedProperty ?? null;

  const buildAdPayload = () => ({
    ...initial,
    grot_volume: String(grotVolume).trim(),
    grot_heap_covered: heapCovered,
    grot_collected: collected,
    grotCommentsExtra: initial.grotCommentsExtra ?? ''
  });

  const handleContinue = () => {
    const fields = {
      grot_volume: grotVolume,
      grot_heap_covered: heapCovered,
      grot_collected: collected
    };
    const missing = grotStep3ValidationMessages(fields);
    if (missing.length > 0) {
      setStepError(
        `Fyll i allt som krävs innan du går vidare:\n- ${missing.join('\n- ')}`
      );
      return;
    }
    setStepError(null);
    dispatch({ type: 'SET_AD_CORE', payload: buildAdPayload() });
    dispatch({ type: 'SET_STEP', step: 4 });
  };

  const handleBack = () => {
    setStepError(null);
    dispatch({ type: 'SET_AD_CORE', payload: buildAdPayload() });
    dispatch({ type: 'SET_STEP', step: 2 });
  };

  const volumeInvalid =
    grotVolume !== '' &&
    (!Number.isFinite(Number(grotVolume)) || Number(grotVolume) <= 0);

  const volumeInfo = SELL_TIMBER_GROT_STEP3_FIELD_INFO.grot_volume;

  return (
    <TailwindWrapper>
      <div className="w-160 max-w-full mx-auto">
        <form
          className="mx-auto min-w-full flex max-w-full flex-col gap-8 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            handleContinue();
          }}
          noValidate
        >
          <div className="flex flex-col gap-2">
            <Text variant="text-xl/leading-normal/medium">Sälj virke</Text>
            <Text variant="text-sm/leading-normal/medium" className="text-charcoal-700">
              Fyll i uppgifterna om biobränslet nedan.
            </Text>
          </div>

          <div className="flex flex-col gap-2">
            <Text variant="text-sm/leading-normal/medium" className="mb-1">
              Fastighetsbeteckning
            </Text>
            {selectedProperty?.designation ? (
              <Text variant="text-base/leading-normal/normal" className="capitalize">
                {selectedProperty.designation}
              </Text>
            ) : (
              <Text variant="text-base/leading-normal/normal" className="text-red-700">
                Välj en fastighet i fastighetsväljaren överst på sidan.
              </Text>
            )}
          </div>

          <div className="flex max-w-[11rem] flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Text variant="text-sm/leading-normal/medium" as="span" id="label-grot-volume">
                Hur många kubik är det?
              </Text>
              <SellTimberFieldInfo
                title={volumeInfo.title}
                text={volumeInfo.text}
                aria-label="Information om hur många kubik det är"
              />
            </div>
            <Input
              tw
              variant="fluid"
              type="number"
              name="grot_volume"
              className="min-w-0 w-full"
              label="Ange volym"
              suffix="m³"
              value={grotVolume}
              onChange={(v) => {
                setStepError(null);
                setGrotVolume(v);
              }}
              isValid={volumeInvalid ? false : undefined}
              aria-labelledby="label-grot-volume"
            />
          </div>

          <GrotJaNejFieldset
            legendId="legend-grot-heap-covered"
            legend="Är biobränslet täckt?"
            name="grot_heap_covered"
            value={heapCovered}
            fieldInfo={SELL_TIMBER_GROT_STEP3_FIELD_INFO.grot_heap_covered}
            onChange={(v) => {
              setStepError(null);
              setHeapCovered(v);
            }}
          />

          <GrotJaNejFieldset
            legendId="legend-grot-collected"
            legend="Är biobränslet uppsamlat?"
            name="grot_collected"
            value={collected}
            fieldInfo={SELL_TIMBER_GROT_STEP3_FIELD_INFO.grot_collected}
            onChange={(v) => {
              setStepError(null);
              setCollected(v);
            }}
          />

          {stepError ? (
            <Text
              variant="text-sm/leading-normal/normal"
              className="whitespace-pre-wrap text-red-700"
              role="alert"
            >
              {stepError}
            </Text>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              tw
              type="button"
              variant="tertiary"
              onClick={handleBack}
            >
              Tillbaka
            </Button>
            <Button type="submit" variant="primary">
              Gå vidare
            </Button>
          </div>
        </form>
      </div>
    </TailwindWrapper>
  );
}
